import { Box, Link as MuiLink, Stack, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { LinkView } from "../../../../utils/icGovernance";
import FieldGrid from "../detail/FieldGrid";
import SideCard from "../detail/SideCard";
import { DetailOf, ItemActionProps } from "../detail/types";
import { FAINT, MONO, TEXT } from "../detail/ui";
import { StatusChip, UAT_RESULT_LABEL, dateOnly, itemUrlById, yesNo } from "../governanceStatus";
import RecordResultForm from "./RecordResultForm";
import ReleaseLink from "./ReleaseLink";

/** Issues linked to the test, each with its status. */
const RelatedIssues = ({ links }: { links: LinkView[] }) => {
  if (links.length === 0) {
    return (
      <Typography component="span" variant="body2" sx={{ color: FAINT }}>
        None
      </Typography>
    );
  }
  return (
    <Stack spacing={0.75}>
      {links.map((l, i) => {
        const label = l.target?.displayId || l.targetDisplayId;
        return (
          <Box key={`${l.targetId}-${i}`} sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <MuiLink
              component={RouterLink}
              to={itemUrlById(label || l.targetId)}
              sx={{ fontFamily: MONO, fontWeight: 700, fontSize: 14 }}
            >
              {label || "Linked issue"}
            </MuiLink>
            {l.target ? <StatusChip kind="ISSUE" status={l.target.status} /> : null}
            {l.target?.title ? (
              <Typography component="span" variant="body2" sx={{ color: TEXT, overflowWrap: "anywhere" }}>
                {l.target.title}
              </Typography>
            ) : null}
          </Box>
        );
      })}
    </Stack>
  );
};

/** Test case + result, with the record-result form when allowed. */
const UatTestPanel = ({ item, onUpdated, onReload }: { item: DetailOf<"UAT_TEST"> } & ItemActionProps) => {
  const issueLinks = (item.links ?? []).filter((l) => (l.target?.kind ?? l.targetKind) === "ISSUE");
  return (
    <Stack spacing={2}>
      <SideCard title="Test case">
        <FieldGrid
          fields={[
            { label: "Release", value: <ReleaseLink release={item.release} />, full: true },
            { label: "Requirement / feature", value: item.requirement, full: true },
            { label: "Test scenario", value: item.scenario, full: true },
            { label: "Preconditions / test data", value: item.preconditions, full: true },
            { label: "Expected result", value: item.expectedResult, full: true },
          ]}
        />
      </SideCard>

      <SideCard title="Result">
        <FieldGrid
          fields={[
            { label: "Result", value: <StatusChip kind="UAT_TEST" status={item.status} /> },
            { label: "Test date", value: dateOnly(item.testDate) },
            { label: "Retest required", value: yesNo(item.retestRequired) },
            { label: "Retest result", value: item.retestResult ? UAT_RESULT_LABEL[item.retestResult] : null },
            { label: "Actual result", value: item.actualResult, full: true },
            { label: "Related issues", value: <RelatedIssues links={issueLinks} />, full: true },
          ]}
        />
        {item.permissions?.recordResult ? (
          <RecordResultForm item={item} onUpdated={onUpdated} onReload={onReload} />
        ) : null}
      </SideCard>
    </Stack>
  );
};

export default UatTestPanel;
